import React, { useEffect, useState } from 'react';
import { useMingo } from '../context/MingoContext';
import { SESSION_RESTORE_MS, waitForSupabaseSession } from '../lib/authSession';
import { clearAuthSessionFlags, isGuestStorage, isLoggedInStorage } from '../lib/onboardingStorage';
import { getSupabase, isSupabaseConfigured } from '../lib/supabase';

/**
 * App entry: wait for Supabase session restore before rendering the router.
 * @param {{ children: React.ReactNode }} props
 */
export function SessionGate({ children }) {
  const { t, refreshAuth } = useMingo();
  const [phase, setPhase] = useState('restoring');

  useEffect(() => {
    let cancelled = false;

    async function run() {
      if (!isSupabaseConfigured) {
        if (!cancelled) setPhase('anon');
        return;
      }

      const sb = getSupabase();
      if (!sb) {
        if (!cancelled) setPhase('anon');
        return;
      }

      const expectSession = isLoggedInStorage() && !isGuestStorage();
      const session = await waitForSupabaseSession(sb, {
        maxMs: SESSION_RESTORE_MS,
        expectSession,
      });
      if (cancelled) return;

      if (session?.user) {
        setPhase('authed');
        return;
      }

      if (expectSession) {
        clearAuthSessionFlags();
        refreshAuth();
      }
      setPhase('anon');
    }

    void run();

    return () => {
      cancelled = true;
    };
  }, [refreshAuth]);

  // NOTE: Do not clearChunkReloadFlag() here. Clearing on every successful boot
  // re-arms tryChunkReloadOnce and can infinite-loop on cold lazy routes (/search)
  // when a stale chunk keeps failing after deploy. Cooldown lives in chunkReloadGuard.

  if (phase === 'restoring') {
    return (
      <div className="mingo-splash mingo-splash--v2" role="status" aria-busy="true" aria-live="polite">
        <div className="mingo-splash__inner">
          <div className="mingo-splash__logo mingo-splash__logo--hero" aria-label={t('common.appName')}>
            Mingo
          </div>
        </div>
      </div>
    );
  }

  return children;
}
